/** Preuve d’intégrité réelle — empreintes SHA-256 + piste d’audit. */
import { useEffect, useState } from "react";
import { facturxFetch } from "../../api/client";
import { Button } from "../../components/Button";
import { CopyButton } from "../../components/CopyButton";
import { ClockLine } from "../../formats/ClockLine";
import { printQuoteIntegrity } from "./quotePdf";

export type QuoteIntegrity = {
  id: number;
  number: string;
  status: string;
  frozen: boolean;
  algo: string;
  content_hash: string;
  computed_hash: string;
  match: boolean;
  validated_at: string | null;
  validated_by_user_id: number | null;
  validated_by?: string;
  viewed_at: string | null;
  signature: {
    signer_name: string;
    phrase: string;
    signed_at: string;
    content_hash: string;
    proof_hash: string;
    ip: string;
    user_agent: string;
    invoice_id: number | null;
  } | null;
  audit: {
    action: string;
    detail: string;
    actor_user_id: number;
    actor_name?: string;
    actor_kind?: string;
    created_at: string;
    clock_tz?: string;
    document_id?: number;
    doc_type?: string;
    doc_number?: string;
  }[];
  chain?: {
    id: number;
    doc_type: string;
    number: string;
    status: string;
    content_hash: string;
    current?: boolean;
  }[];
  checked_at?: string;
  timezone?: string;
};

function chainLabel(type: string, number: string): string {
  const name = type === "quote" ? "Devis" : type === "credit_note" ? "Avoir" : "Facture";
  return number ? `${name} ${number}` : name;
}

function auditLabel(action: string): string {
  if (action === "document.validate") return "Figé / envoyé";
  if (action === "quote.sign") return "Signature électronique";
  if (action === "quote.refuse") return "Devis refusé";
  if (action === "document.convert") return "Conversion en facture";
  if (action === "document.credit") return "Annulation / avoir";
  if (action === "payment.create") return "Paiement enregistré";
  if (action === "payment.update") return "Paiement modifié";
  if (action === "payment.refund") return "Remboursement";
  if (action === "payment.send") return "Reçu envoyé";
  if (action === "quote.send" || action === "document.send" || action === "payment.send" || action === "reminder.send") return "E-mail";
  if (action === "document.send_fail" || action === "payment.send_fail" || action === "reminder.send_fail") return "E-mail non parti";
  if (action === "document.view" || action === "quote.view") return "Première consultation";
  if (action === "document.revisit") return "Nouvelle consultation";
  return action;
}

function hashGroups(hex: string): string {
  return hex.replace(/\s/g, "").toLowerCase().replace(/(.{8})/g, "$1 ").trim();
}

function cleanDetail(detail: string): string {
  return detail.replace(/\s*[a-f0-9]{64}\s*/gi, " ").trim();
}

function actorName(row: QuoteIntegrity["audit"][number]): string {
  const name = (row.actor_name || "").trim();
  if (name) return row.actor_kind === "client" ? `Client · ${name}` : name;
  return row.actor_user_id ? `Utilisateur #${row.actor_user_id}` : "";
}

function HashCard({ label, hint, value }: { label: string; hint: string; value: string }) {
  if (!value) return null;
  return (
    <div className="fx-trace__hash">
      <div className="fx-trace__hash-top">
        <div>
          <p className="fx-trace__hash-label">{label}</p>
          <p className="fx-trace__hash-hint">{hint}</p>
        </div>
        <CopyButton text={value} label="Copier l’empreinte" />
      </div>
      <code>{hashGroups(value)}</code>
    </div>
  );
}

type QuoteTraceDrawerProps = {
  id: number;
  onClose: () => void;
};

export function QuoteTraceDrawer({ id, onClose }: QuoteTraceDrawerProps) {
  const [out, setOut] = useState(false);
  const [data, setData] = useState<QuoteIntegrity | null>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    facturxFetch<{ integrity: QuoteIntegrity }>(`documents.php?id=${id}&action=integrity`)
      .then((res) => {
        if (live) setData(res.integrity);
      })
      .catch((e) => {
        if (live) setErr(e instanceof Error ? e.message : "Preuve indisponible.");
      });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOut(true);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      live = false;
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [id]);

  function close() {
    if (!out) setOut(true);
  }

  const verdict = !data
    ? null
    : !data.frozen
      ? { kind: "draft", title: "Pièce non figée", text: "Brouillon : aucune empreinte SHA-256 n’a encore été enregistrée." }
      : data.match
        ? { kind: "ok", title: "Empreinte intacte", text: "Le snapshot recalculé est identique à celui enregistré à la validation." }
        : { kind: "bad", title: "Empreinte altérée", text: "Le hash recalculé ne correspond pas. La pièce a pu être modifiée hors application." };

  return (
    <div className={["fx-quote-pdf fx-trace", out ? "is-out" : ""].filter(Boolean).join(" ")} role="dialog" aria-modal="true" aria-label="Preuve d’intégrité">
      <button type="button" className="fx-quote-pdf__backdrop" aria-label="Fermer" onClick={close} />
      <aside
        className="fx-quote-pdf__sheet fx-trace__sheet"
        onAnimationEnd={(e) => {
          if (e.target === e.currentTarget && out) onClose();
        }}
      >
        <header className="fx-quote-stage__pdf-bar">
          <div className="fx-trace__head">
            <p>Preuve d’intégrité</p>
            {data?.number ? <span>{data.number}</span> : null}
          </div>
          <div className="fx-quote-stage__pdf-actions">
            {data ? (
              <Button className="fx-quote-stage__pdf-print" onClick={() => printQuoteIntegrity(id, `Preuve-${data.number || id}`)}>
                Imprimer
              </Button>
            ) : null}
            <button type="button" className="fx-quote-icon" aria-label="Fermer" onClick={close}>
              ×
            </button>
          </div>
        </header>
        <div className="fx-trace__body">
          {err ? <p className="fx-trace__err">{err}</p> : null}
          {!data && !err ? <p className="fx-trace__muted">Lecture des empreintes…</p> : null}
          {data && verdict ? (
            <>
              <div className={`fx-trace__verdict is-${verdict.kind}`}>
                <span className="fx-trace__verdict-ico" aria-hidden="true">
                  {verdict.kind === "ok" ? (
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M12 3l7 3v6c0 5-3.2 8.2-7 9-3.8-.8-7-4-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.7" />
                      <path d="m8.4 12 2.5 2.5 4.7-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : verdict.kind === "bad" ? (
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M12 3l7 3v6c0 5-3.2 8.2-7 9-3.8-.8-7-4-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.7" />
                      <path d="M9.2 9.2 14.8 14.8M14.8 9.2 9.2 14.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M12 3l7 3v6c0 5-3.2 8.2-7 9-3.8-.8-7-4-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.7" />
                    </svg>
                  )}
                </span>
                <div>
                  <strong>{verdict.title}</strong>
                  <p>{verdict.text}</p>
                </div>
                <em>{data.algo || "SHA-256"}</em>
              </div>
              <p className="fx-trace__clock">
                Contrôle <ClockLine iso={data.checked_at} zone={data.timezone || "UTC"} />
              </p>

              <dl className="fx-trace__meta">
                <div>
                  <dt>Validé</dt>
                  <dd>
                    <ClockLine iso={data.validated_at} zone="Europe/Paris" extra={data.validated_by || undefined} />
                  </dd>
                </div>
                <div>
                  <dt>Première consultation</dt>
                  <dd>
                    <ClockLine iso={data.viewed_at} zone="Europe/Paris" extra="Première ouverture portail" />
                  </dd>
                </div>
                <div>
                  <dt>État</dt>
                  <dd>{data.frozen ? "Figé" : "Brouillon"}</dd>
                </div>
              </dl>

              {data.frozen ? (
                <section className="fx-trace__block">
                  <h3>Empreinte du contenu</h3>
                  <p className="fx-trace__lead">
                    Empreinte SHA-256 de la pièce figée : client, lignes, remises, totaux. Si une ligne bouge, ce hash change.
                  </p>
                  <div className="fx-trace__hashes">
                    <HashCard label="Enregistrée à la validation" hint="Celle qui a figé la pièce" value={data.content_hash} />
                    <HashCard label="Recalculée maintenant" hint="Doit être identique si rien n’a bougé" value={data.computed_hash} />
                  </div>
                </section>
              ) : null}

              {data.signature ? (
                <section className="fx-trace__block">
                  <h3>Signature électronique simple</h3>
                  <div className="fx-trace__sign">
                    <p className="fx-trace__sign-name">{data.signature.signer_name}</p>
                    <p className="fx-trace__sign-when">
                      <ClockLine iso={data.signature.signed_at} zone="UTC" />
                    </p>
                    {data.signature.phrase ? <blockquote>{data.signature.phrase}</blockquote> : null}
                    <div className="fx-trace__sign-meta">
                      {data.signature.ip ? <span>IP {data.signature.ip}</span> : null}
                      {data.signature.invoice_id ? <span>Facture #{data.signature.invoice_id}</span> : null}
                    </div>
                    <p className="fx-trace__lead">
                      Empreinte de l’acte : qui a signé, quelle mention, à quelle heure, depuis quelle IP — collée au hash du devis ci-dessus.
                    </p>
                    <HashCard label="Empreinte de preuve" hint="Celle de la signature, pas du devis" value={data.signature.proof_hash} />
                    {data.signature.user_agent ? <p className="fx-trace__ua">{data.signature.user_agent}</p> : null}
                    <p className="fx-trace__legal">
                      Signature électronique simple · art. 1367 du Code civil. Horodatée côté serveur et liée à l’empreinte de cette pièce.
                    </p>
                  </div>
                </section>
              ) : null}

              {data.chain && data.chain.length > 1 ? (
                <section className="fx-trace__block">
                  <h3>Pièces liées</h3>
                  <p className="fx-trace__lead">
                    La preuve suit le fil devis → facture → avoir. Chaque pièce garde sa propre empreinte.
                  </p>
                  <ol className="fx-trace__audit">
                    {data.chain.map((piece) => (
                      <li key={piece.id}>
                        <strong>
                          {chainLabel(piece.doc_type, piece.number)}
                          {piece.current ? " · cette pièce" : ""}
                        </strong>
                        <span>{piece.content_hash ? hashGroups(piece.content_hash) : piece.status === "draft" ? "Brouillon, pas encore d’empreinte" : ""}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              ) : null}

              {data.audit.length > 0 ? (
                <section className="fx-trace__block">
                  <h3>Chronologie</h3>
                  <ol className="fx-trace__audit">
                    {data.audit.map((row, i) => (
                      <li key={`${row.created_at}-${i}`}>
                        <strong>{auditLabel(row.action)}</strong>
                        <span>
                          <ClockLine iso={row.created_at} zone={row.clock_tz || (row.action === "quote.sign" || row.action === "quote.refuse" ? "UTC" : "Europe/Paris")} extra={[row.doc_number || row.doc_type ? chainLabel(row.doc_type || "", row.doc_number || "") : "", row.action === "quote.sign" || row.action === "document.view" || row.action === "quote.view" || row.action === "document.revisit" ? "" : cleanDetail(row.detail), actorName(row)].filter(Boolean).join(" · ") || undefined} />
                        </span>
                      </li>
                    ))}
                  </ol>
                </section>
              ) : null}
            </>
          ) : null}
        </div>
        <div className="fx-trace__foot">
          <Button variant="ghost" onClick={close}>
            Fermer
          </Button>
        </div>
      </aside>
    </div>
  );
}
